import { useEffect, useState } from 'react';
import { getCatalogProductDetail } from '../../services/api/tryOn';
import type { TryOnProductDetail } from '../../types/tryOn';
import { getCachedProductDetail, setCachedProductDetail } from './tryOnSession';
import { t } from '../../utils/ui-strings';

interface TryOnProductDetailsProps {
  productId: string;
  lang?: string;
}

/**
 * "Produktbeskrivning"-accordion i resultatvyn: förhämtar produktdetaljer i
 * bakgrunden direkt när resultatvyn visas (cachas per produkt i
 * sessionsminnet) så att expanderingen är omedelbar. Kollapsad som default
 * så AI-bilden förblir hjälten.
 */

/** Avkodar HTML-entiteter (&#229; → å) OCH strippar taggar via en textarea. */
export function decodeAndStripHtml(value: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value.replace(/<[^>]*>/g, ' ');
  return textarea.value.replace(/\s+/g, ' ').trim();
}

/** Prefetch + sessionscache av produktdetaljer. Delas av steg 1 och resultatvyn. */
export function useProductDetail(
  productId: string,
  lang?: string,
): {
  detail: TryOnProductDetail | null | undefined;
  isLoading: boolean;
} {
  const [detail, setDetail] = useState<TryOnProductDetail | null | undefined>(
    getCachedProductDetail(productId, lang),
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const cached = getCachedProductDetail(productId, lang);
    setDetail(cached);
    if (cached !== undefined) return;

    let cancelled = false;
    setIsLoading(true);
    getCatalogProductDetail(productId, lang)
      .then((fetched) => {
        setCachedProductDetail(productId, lang, fetched);
        if (!cancelled) setDetail(fetched);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, lang]);

  return { detail, isLoading };
}

/**
 * Endast kundrelevanta properties visas (allowlist). Interna admin-/PIM-fält
 * (Klar för publicering, Masterartikel, synk-flaggor, lev.-nummer osv.) och
 * dubbletter av egna rader (passform, tvättråd, material) filtreras bort.
 */
// Matchar svenska PROPERTYNAMN ur katalogen, inte UI-text. Askås fältnamn är
// svenska oavsett kundens språk — översätts mönstret slutar
// egenskapsfiltreringen fungera på alla marknader.
const ALLOWED_PROPERTY_PATTERN =
  /^(färg|ärmslut|sprund|foder|fickor|tillverkningsland|mönster|årstid|krage|knappar|stängning|längd|midja|design|stil)$/i; // i18n-ignore

/** Plattar Bloomreach-properties (olika former) till label/värde-par. */
function flattenProperties(properties: unknown): Array<{ label: string; value: string }> {
  if (!Array.isArray(properties)) return [];
  const rows: Array<{ label: string; value: string }> = [];
  const seen = new Set<string>();

  for (const property of properties) {
    if (!property || typeof property !== 'object') continue;
    const propertyName = String((property as any).name ?? '').trim();
    const content = (property as any).content;

    let label = propertyName;
    // Allowlisten matchar svenska namn; efter översättning bär posten
    // originalName. Filtrera på originalet, visa det översatta.
    let filterLabel = propertyName;
    let value = '';

    if (Array.isArray(content)) {
      value = content
        .map((entry: any) => String(entry?.value ?? '').trim())
        .filter(Boolean)
        .join(', ');
      if (!label) {
        label = content
          .map((entry: any) => String(entry?.name ?? '').trim())
          .find(Boolean) ?? '';
      }
      const original = content
        .map((entry: any) => String(entry?.originalName ?? '').trim())
        .find(Boolean);
      filterLabel = original || label;
    } else {
      value = String((property as any).value ?? '').trim();
    }

    if (!label || !value) continue;
    if (!ALLOWED_PROPERTY_PATTERN.test(filterLabel)) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ label, value: decodeAndStripHtml(value) });
  }

  return rows;
}

/** Materialstext utan skräpposter ("Passform: Smal passform" läcker in i arrayen). */
export function formatDetailMaterials(detail: TryOnProductDetail | null | undefined): string {
  const materials = detail?.materials;
  if (typeof materials === 'string') return materials.trim();
  if (Array.isArray(materials)) {
    const parts = materials
      .map((m: any) => {
        if (typeof m === 'string') return m.trim();
        const name = String(m?.name ?? '').trim();
        if (!name) return '';
        const pct = m?.percentage ?? m?.percemtage;
        return typeof pct === 'number' && pct > 0 ? `${name} ${pct}%` : name;
      })
      .filter((part) => part && !part.includes(':'));
    if (parts.length > 0) return parts.join(', ');
  }
  return typeof detail?.composition === 'string' ? detail.composition.trim() : '';
}

export function TryOnProductDetails({ productId, lang }: TryOnProductDetailsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { detail, isLoading } = useProductDetail(productId, lang);

  useEffect(() => {
    setIsOpen(false);
  }, [productId, lang]);

  const description = detail?.description ? decodeAndStripHtml(String(detail.description)) : '';
  const materials = formatDetailMaterials(detail);
  const propertyRows = flattenProperties(detail?.properties);

  return (
    <div className="rounded-2xl bg-white shadow-inner">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-blue">
          {t('try_on_details_title', lang)}
        </span>
        <span className="text-brand-blue">{isOpen ? '−' : '+'}</span>
      </button>

      {isOpen ? (
        <div className="space-y-3 px-3 pb-3 text-sm text-brand-blue">
          {isLoading ? (
            <p className="text-xs text-text-grey">{t('try_on_details_loading', lang)}</p>
          ) : detail ? (
            <>
              {description ? <p className="text-xs leading-relaxed">{description}</p> : null}

              {materials ? (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-text-grey">
                    {t('try_on_material', lang)}
                  </p>
                  <p className="text-xs">{materials}</p>
                </div>
              ) : null}

              {detail.fit ? (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-text-grey">
                    {t('try_on_fit', lang)}
                  </p>
                  <p className="text-xs">{detail.fit}</p>
                </div>
              ) : null}

              {detail.care ? (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-text-grey">
                    {t('try_on_care', lang)}
                  </p>
                  <p className="text-xs">{decodeAndStripHtml(detail.care)}</p>
                </div>
              ) : null}

              {propertyRows.length > 0 ? (
                <dl className="space-y-1">
                  {propertyRows.map((row) => (
                    <div key={row.label} className="text-xs">
                      <dt className="inline font-medium">{row.label}: </dt>
                      <dd className="inline text-text-grey">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {detail.sku ? (
                <p className="text-[11px] text-text-grey">{t('try_on_sku', lang)}: {detail.sku}</p>
              ) : null}
            </>
          ) : (
            <p className="text-xs text-text-grey">
              {t('try_on_details_error', lang)}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
